import React, { createContext, useContext } from 'react';
import { GameState, Player, TransferPlayer, TacticalMentality, TrainingFocus, DisplayFixture, Tactics, AttackingFocus, PassingStyle, DefensiveStyle, TacklingStyle, Position, SearchHistoryItem } from '../domain/entities';
import { SaveFile } from '../infrastructure/GameStateRepository';
import { SackedInfo } from '../application/hooks/useGameSimulation';

export interface GameActions {
    startGame: () => void;
    startNewGame: (data: any | null, gameType: 'mens' | 'womens') => void;
    startChallengeGame: (type: 'great_escape' | 'youth_academy' | 'debt_crisis', gameType: 'mens' | 'womens') => void;
    resetAndGoToStart: () => void;
    playWeek: (isAiPunditEnabled?: boolean) => void;
    advanceSeason: () => void;
    continueAfterSacking: (sackedInfo: SackedInfo) => void;
    updateTactics: (formation: string, mentality: TacticalMentality, lineup: Tactics['lineup'], attackingFocus: AttackingFocus, passingStyle: PassingStyle, defensiveStyle: DefensiveStyle, tacklingStyle: TacklingStyle) => void;
    updateTeamTrainingFocus: (focus: TrainingFocus) => void;
    updatePositionalTrainingFocus: (position: Position, focus: TrainingFocus | null) => void;
    updatePlayerTrainingFocus: (playerId: number, focus: TrainingFocus | null) => void;
    upgradeTrainingFacilities: () => Promise<{ success: boolean; message: string; }>;
    downgradeTrainingFacilities: () => Promise<{ success: boolean; message: string; }>;
    renewContract: (playerId: number, newWage: number, newLength: number) => Promise<{ success: boolean; message: string; }>;
    restartGame: () => void;
    hardResetGame: () => void;
    negotiatePurchase: (player: TransferPlayer, offerMultiplier: number) => Promise<{ success: boolean; message: string; acceptedOffer?: number; }>;
    finalizePurchase: (player: TransferPlayer, transferFee: number, wageMultiplier: number) => Promise<{ success: boolean; message: string; }>;
    sellPlayer: (player: Player, priceMultiplier?: number) => Promise<{ success: boolean; message: string; }>;
    unlistPlayer: (player: Player) => Promise<{ success: boolean; message: string; }>;
    releasePlayer: (player: Player) => Promise<{ success: boolean; message: string; }>;
    upgradeYouthAcademy: () => Promise<{ success: boolean; message: string; }>;
    downgradeYouthAcademy: () => Promise<{ success: boolean; message: string; }>;
    promoteYouthPlayer: (player: Player) => Promise<{ success: boolean; message: string; }>;
    updateYouthPreferences: (preferences: { roleFocus?: string; footPreference?: string; widthFocus?: string; playingStyle?: string; }) => void;
    loadFullGameState: (newState: GameState) => void;
    listSaves: () => Promise<SaveFile[]>;
    loadNamedGame: (id: number) => Promise<boolean>;
    saveNamedGame: (id: number | null, name: string) => Promise<boolean>;
    deleteNamedGame: (id: number) => Promise<void>;
    markNewsAsRead: (id?: string) => void;
    simulateFullSeason: () => void;
    simulateFourWeeks: () => void;
    simulateToCupFinal: () => void;
    simulateToSeasonWin: () => void;
    addPlayerMoney: (amount: number) => void;
    setDebugFlag: (flag: keyof NonNullable<GameState['debugFlags']>) => void;
    toggleDebugFlag: (flag: keyof NonNullable<GameState['debugFlags']>) => void;
    updateManagerName: (name: string) => void;
    handlePlayerConversation: (playerId: number, choice: 'praise' | 'promise' | 'show_faith' | 'demand_improvement' | 'warn_dropped' | 'dismiss_concerns' | 'assert_authority') => Promise<{ success: boolean; message: string; }>;
    offerFinancialIncentive: (playerId: number, type: 'wage' | 'bonus', amount: number) => Promise<{ success: boolean; message: string; }>;
    cacheAiPunditTake: (playerId: number, take: string) => void;
    cacheAiMatchReport: (fixture: DisplayFixture, report: { title: string; message: string; }) => void;
    cacheAiPlayerRoleAnalysis: (playerId: number, analysis: { roleName: string; roleDescription: string; }) => void;
    dismissSimulation: (shouldApplyState?: boolean) => void;
    addElitePlayer: () => Promise<Player | undefined>;
    addLegendPlayer: () => Promise<Player | undefined>;
    generateGreatYouthIntake: () => void;
    boostTeamMorale: () => void;
    healAllInjuries: () => void;
    restartWithTeamCount: (teamCount: number) => Promise<void>;
    commitPendingState: () => void;
    acceptIncomingBid: (bidId: string) => Promise<{ success: boolean; message: string; }>;
    rejectIncomingBid: (bidId: string) => Promise<{ success: boolean; message: string; }>;
    negotiateIncomingBid: (bidId: string, percentageIncrease: number) => Promise<{ success: boolean; message: string; }>;
    respondToIncomingBidNegotiation: (bidId: string, accept: boolean) => Promise<{ success: boolean; message: string; }>;
    submitFreeAgentBid: (freeAgentId: number, wage: number) => Promise<{ success: boolean; message: string; }>;
    updateSearchHistory: (item: SearchHistoryItem) => void;
    acceptTransferSuggestion: (suggestionId: string) => Promise<{ success: boolean; message: string; }>;
    rejectTransferSuggestion: (suggestionId: string) => Promise<{ success: boolean; message: string; }>;
    adjustListingPrice: (player: Player, newPrice: number) => Promise<{ success: boolean; message: string; }>;
}

const GameActionsContext = createContext<GameActions | undefined>(undefined);

export const GameActionsProvider: React.FC<{ children: React.ReactNode, actions: GameActions }> = ({ children, actions }) => {
    return (
        <GameActionsContext.Provider value={actions}>
            {children}
        </GameActionsContext.Provider>
    );
};

export const useGameActions = () => {
    const context = useContext(GameActionsContext);
    if (!context) {
        throw new Error('useGameActions must be used within a GameActionsProvider');
    }
    return context;
};
